import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import ContactAvatar from "../../components/ContactAvatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import AddIcon from "@material-ui/icons/Add";
import PublishIcon from "@material-ui/icons/Publish";
import GetAppIcon from "@material-ui/icons/GetApp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";

import api from "../../services/api";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { socketConnection } from "../../services/socket";
import EmptyState from "../../components/EmptyState";

import { useTheme, useMediaQuery, Tooltip } from "@material-ui/core";
import formatPhone from "../../helpers/formatPhone";
import Chip from "@material-ui/core/Chip";

const tagChipStyle = (tag) => ({
  background: tag.color || "#999",
  color: "#fff",
  fontWeight: 600,
  borderRadius: 12,
  fontSize: "0.7em",
  height: 20,
  marginRight: 4,
  marginTop: 2,
});

const ContactTagChips = ({ tags }) =>
  tags && tags.length > 0 ? (
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      {tags.map((tag) => (
        <Chip key={tag.id} size="small" label={tag.name} style={tagChipStyle(tag)} />
      ))}
    </div>
  ) : null;

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // mescla: alguns eventos do socket chegam sem as tags do contato
      state[contactIndex] = { ...state[contactIndex], ...contact };
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  // com filtro ativo: só atualiza quem já está na lista
  if (action.type === "UPDATE_IF_LISTED") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);
    if (contactIndex === -1) return state;
    state[contactIndex] = { ...state[contactIndex], ...contact };
    return [...state];
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  header: {
    color: theme.palette.text.primary,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0, 0.75, 2),
  },
  headerTitle: {
    "& h5": { marginBottom: 0, fontWeight: 600 },
  },
  headerButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      "& > *": { flex: "1 1 auto" },
    },
  },
  headerButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2),
  },
  secondaryButton: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 14,
    width: "100%",
    boxSizing: "border-box",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    flex: "1 1 280px",
    maxWidth: 420,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
    },
    [theme.breakpoints.down("xs")]: { maxWidth: "100%" },
  },
  tagFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  filterChip: {
    fontWeight: 500,
    height: 32,
    borderRadius: 16,
  },
  tagDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    marginLeft: 8,
  },
  tableWrapper: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
    ...sidebarScrollbar(theme),
  },
  headCell: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    backgroundColor: theme.palette.background.paper,
    whiteSpace: "nowrap",
  },
  row: {
    "& > td": {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
    },
    transition: "background-color .12s",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    cursor: "pointer",
    minWidth: 0,
  },
  contactName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    "&:hover": { textDecoration: "underline" },
  },
  phone: {
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  addEmailLink: {
    padding: 0,
    minWidth: 0,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 500,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 2,
  },
  chatButton: {
    textTransform: "none",
    fontWeight: 500,
    borderRadius: 8,
    marginRight: theme.spacing(0.5),
    whiteSpace: "nowrap",
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
    },
  },
  iconAction: {
    color: theme.palette.text.secondary,
    "&:hover": { color: theme.palette.text.primary },
  },
  deleteAction: {
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, 0.08),
    },
  },
  footer: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.75, 1.5),
      fontSize: 12,
    },
  },

  // ---- Mobile: layout compacto para sobrar espaço para a lista ----
  mobileHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 0.5, 1.5),
    "& h5": { marginBottom: 0, fontWeight: 600, lineHeight: 1.2 },
  },
  mobileHeaderTitle: {
    flex: 1,
    minWidth: 0,
  },
  mobileToolbar: {
    padding: theme.spacing(1.25, 1.5),
    gap: theme.spacing(1),
  },
  // tags em uma única linha com rolagem horizontal
  tagFiltersScroll: {
    flexWrap: "nowrap",
    overflowX: "auto",
    width: "100%",
    paddingBottom: 2,
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    "& > *": { flexShrink: 0 },
  },
  mobileList: {
    padding: theme.spacing(0, 0.5),
  },
  mobileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  mobileRowInfo: {
    flex: 1,
    minWidth: 0,
    cursor: "pointer",
  },
  ellipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  mobileChatButton: {
    color: "#25D366",
    backgroundColor: alpha("#25D366", 0.1),
    "&:hover": { backgroundColor: alpha("#25D366", 0.2) },
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [tagOptions, setTagOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const tagIdsParam = selectedTags.map((t) => t.id).join(",");
  const hasFilter = Boolean(searchParam.trim() || tagIdsParam);
  // usado pelo socket: com filtro ativo, não insere contatos novos na lista
  const hasFilterRef = useRef(false);
  hasFilterRef.current = hasFilter;
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  // mobile: menu "⋮" do cabeçalho e menu de ações de cada contato
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState(null);
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, contact: null });
  const csvInputRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, tagIdsParam]);

  useEffect(() => {
    api
      .get("/tags/list")
      // tags de Kanban são etapas do atendimento, não do contato
      .then(({ data }) => setTagOptions((data || []).filter((t) => t.kanban !== 1)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: {
              searchParam,
              pageNumber,
              ...(tagIdsParam ? { tags: tagIdsParam } : {}),
            },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          if (typeof data.count === "number") setTotalCount(data.count);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, tagIdsParam]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({
          type: hasFilterRef.current ? "UPDATE_IF_LISTED" : "UPDATE_CONTACTS",
          payload: data.contact,
        });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const toggleTagFilter = (tag) => {
    setSelectedTags((prev) =>
      prev.some((t) => t.id === tag.id)
        ? prev.filter((t) => t.id !== tag.id)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchParam("");
    setSelectedTags([]);
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleOpenContactProfile = (contactId) => {
    history.push(`/contacts/${contactId}/profile`);
  };

  const handleStartChat = (contact) => {
    setContactTicket(contact);
    setNewTicketModalOpen(true);
  };

  const handleAskDelete = (contact) => {
    setDeletingContact(contact);
    setConfirmOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleimportContact = async () => {
    try {
      setLoading(true);
      await api.post("/contacts/import");
      toast.success("Contatos importados com sucesso!");
      history.go(0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromPhone = () => {
    setImportAnchorEl(null);
    setHeaderMenuAnchorEl(null);
    setDeletingContact(null);
    setConfirmOpen(true);
  };

  const handlePickCsv = () => {
    setImportAnchorEl(null);
    setHeaderMenuAnchorEl(null);
    if (csvInputRef.current) csvInputRef.current.click();
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      setLoading(true);
      const { data } = await api.post("/contacts/import-csv", formData);
      toast.success(
        `Planilha importada: ${data.created} criado(s), ${data.skipped} já existente(s), ${data.invalid} inválido(s).`
      );
      setSearchParam("");
      setPageNumber(1);
      dispatch({ type: "RESET" });
      history.go(0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      toast.info("Exportando contatos...");
      const { data } = await api.get("/contacts/export-csv", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "contatos-messageexpress.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const countLabel =
    totalCount === null
      ? ""
      : hasFilter
      ? `${totalCount} ${totalCount === 1 ? "contato encontrado" : "contatos encontrados"}`
      : `${totalCount} ${totalCount === 1 ? "contato" : "contatos"} na sua base`;

  const renderSecondaryActions = (contact, size = "small") => (
    <>
      <Tooltip title="Ver histórico">
        <IconButton
          size={size}
          className={classes.iconAction}
          onClick={() => handleOpenContactProfile(contact.id)}
          aria-label="Ver histórico"
        >
          <AssignmentIndOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar contato">
        <IconButton
          size={size}
          className={classes.iconAction}
          onClick={() => hadleEditContact(contact.id)}
          aria-label="Editar contato"
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Can
        role={user.profile}
        perform="contacts-page:deleteContact"
        yes={() => (
          <Tooltip title="Excluir contato">
            <IconButton
              size={size}
              className={classes.deleteAction}
              onClick={() => handleAskDelete(contact)}
              aria-label="Excluir contato"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      />
    </>
  );

  // mobile: ações secundárias do contato no menu "⋮"
  const renderRowMenuItems = () => {
    const contact = rowMenu.contact;
    if (!contact) return [];
    const closeAnd = (fn) => () => {
      setRowMenu({ anchorEl: null, contact: null });
      fn();
    };
    const items = [
      <MenuItem key="history" onClick={closeAnd(() => handleOpenContactProfile(contact.id))}>
        <ListItemIcon>
          <AssignmentIndOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Ver histórico" />
      </MenuItem>,
      <MenuItem key="edit" onClick={closeAnd(() => hadleEditContact(contact.id))}>
        <ListItemIcon>
          <EditOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Editar contato" />
      </MenuItem>,
    ];
    const canDelete = Can({
      role: user.profile,
      perform: "contacts-page:deleteContact",
      yes: () => true,
      no: () => false,
    });
    if (canDelete) {
      items.push(
        <MenuItem
          key="delete"
          onClick={closeAnd(() => handleAskDelete(contact))}
          style={{ color: theme.palette.error.main }}
        >
          <ListItemIcon style={{ color: theme.palette.error.main }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Excluir contato" />
        </MenuItem>
      );
    }
    return items;
  };

  const renderChatButton = (contact) => (
    <Tooltip title={contact.number ? "" : "Contato sem WhatsApp"}>
      <span>
        <Button
          size="small"
          className={classes.chatButton}
          startIcon={<WhatsAppIcon fontSize="small" />}
          disabled={!contact.number}
          onClick={() => handleStartChat(contact)}
        >
          Conversar
        </Button>
      </span>
    </Tooltip>
  );

  const emptyState = hasFilter ? (
    <EmptyState
      icon="🔎"
      title="Nenhum contato encontrado"
      description="Nenhum contato corresponde à busca ou às etiquetas selecionadas."
      actionLabel="Limpar filtros"
      onAction={clearFilters}
    />
  ) : (
    <EmptyState
      icon="👥"
      title="Nenhum contato ainda"
      description="Importe sua lista de contatos ou aguarde clientes entrarem em contato via WhatsApp."
      actionLabel="+ Novo Contato"
      onAction={handleOpenContactModal}
    />
  );

  return (
    <MainContainer>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={(e) =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={handleImportCsv}
      />

      {/* Cabeçalho: título + ações principais */}
      {isMobile ? (
        <div className={classes.mobileHeader}>
          <div className={classes.mobileHeaderTitle}>
            <Title>{i18n.t("contacts.title")}</Title>
            {countLabel && (
              <Typography variant="caption" className={classes.muted}>
                {countLabel}
              </Typography>
            )}
          </div>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            size="small"
            className={classes.headerButton}
            style={{ height: 36 }}
            startIcon={<AddIcon />}
            onClick={handleOpenContactModal}
          >
            Novo
          </Button>
          <IconButton
            size="small"
            aria-label="Mais opções"
            aria-haspopup="true"
            aria-controls="contacts-header-menu"
            onClick={(e) => setHeaderMenuAnchorEl(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="contacts-header-menu"
            anchorEl={headerMenuAnchorEl}
            open={Boolean(headerMenuAnchorEl)}
            onClose={() => setHeaderMenuAnchorEl(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={handleImportFromPhone}>
              <ListItemIcon>
                <PhoneAndroidIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Importar do WhatsApp" />
            </MenuItem>
            <MenuItem onClick={handlePickCsv}>
              <ListItemIcon>
                <PublishIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Importar planilha CSV" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setHeaderMenuAnchorEl(null);
                handleExportCsv();
              }}
            >
              <ListItemIcon>
                <GetAppIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Exportar CSV" />
            </MenuItem>
          </Menu>
        </div>
      ) : (
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>{i18n.t("contacts.title")}</Title>
          {countLabel && (
            <Typography variant="body2" className={classes.muted}>
              {countLabel}
            </Typography>
          )}
        </div>
        <div className={classes.headerButtons}>
          <Button
            variant="outlined"
            className={`${classes.headerButton} ${classes.secondaryButton}`}
            startIcon={<PublishIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => setImportAnchorEl(e.currentTarget)}
            aria-haspopup="true"
            aria-controls="contacts-import-menu"
          >
            Importar
          </Button>
          <Menu
            id="contacts-import-menu"
            anchorEl={importAnchorEl}
            keepMounted
            open={Boolean(importAnchorEl)}
            onClose={() => setImportAnchorEl(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem onClick={handleImportFromPhone}>
              <ListItemIcon>
                <PhoneAndroidIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Do WhatsApp conectado"
                secondary="Importa os contatos do telefone"
              />
            </MenuItem>
            <MenuItem onClick={handlePickCsv}>
              <ListItemIcon>
                <InsertDriveFileOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="De uma planilha CSV"
                secondary="Envie um arquivo .csv"
              />
            </MenuItem>
          </Menu>
          <Tooltip title="Exportar todos os contatos (CSV)">
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton}`}
              startIcon={<GetAppIcon />}
              onClick={handleExportCsv}
            >
              Exportar
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={handleOpenContactModal}
          >
            Novo contato
          </Button>
        </div>
      </div>
      )}

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Busca + filtro por tag */}
        <div
          className={`${classes.toolbar} ${isMobile ? classes.mobileToolbar : ""}`}
        >
          <TextField
            className={classes.searchField}
            placeholder="Buscar por nome, telefone ou e-mail"
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            inputProps={{ "aria-label": "Buscar contatos" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          {tagOptions.length > 0 && (
            <div
              className={`${classes.tagFilters} ${isMobile ? classes.tagFiltersScroll : ""}`}
              role="group"
              aria-label="Filtrar por etiqueta"
            >
              <Chip
                clickable
                className={classes.filterChip}
                label="Todos"
                color={selectedTags.length === 0 ? "primary" : "default"}
                variant={selectedTags.length === 0 ? "default" : "outlined"}
                onClick={() => setSelectedTags([])}
                aria-pressed={selectedTags.length === 0}
              />
              {tagOptions.map((tag) => {
                const active = selectedTags.some((t) => t.id === tag.id);
                return (
                  <Chip
                    key={tag.id}
                    clickable
                    className={classes.filterChip}
                    label={tag.name}
                    variant={active ? "default" : "outlined"}
                    onClick={() => toggleTagFilter(tag)}
                    aria-pressed={active}
                    icon={
                      active ? undefined : (
                        <span
                          className={classes.tagDot}
                          style={{ backgroundColor: tag.color || "#999" }}
                        />
                      )
                    }
                    style={
                      active
                        ? { backgroundColor: tag.color || "#999", color: "#fff" }
                        : undefined
                    }
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className={classes.tableWrapper} onScroll={handleScroll}>
          {isMobile ? (
            <div className={classes.mobileList}>
              {contacts.length === 0 && !loading
                ? emptyState
                : contacts.map((contact) => (
                    <div key={contact.id} className={classes.mobileRow}>
                      <ContactAvatar
                        src={contact.profilePicUrl}
                        name={contact.name}
                        style={{ width: 40, height: 40 }}
                      />
                      <div
                        className={classes.mobileRowInfo}
                        onClick={() => handleOpenContactProfile(contact.id)}
                      >
                        <div className={`${classes.contactName} ${classes.ellipsis}`}>
                          {contact.name}
                        </div>
                        <div
                          className={`${classes.phone} ${classes.muted} ${classes.ellipsis}`}
                          style={{ fontSize: 13 }}
                        >
                          {formatPhone(contact.number) || "Sem WhatsApp"}
                        </div>
                        <ContactTagChips tags={contact.tags} />
                      </div>
                      <IconButton
                        size="small"
                        className={classes.mobileChatButton}
                        disabled={!contact.number}
                        onClick={() => handleStartChat(contact)}
                        aria-label="Conversar no WhatsApp"
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className={classes.iconAction}
                        aria-label="Mais ações"
                        onClick={(e) =>
                          setRowMenu({ anchorEl: e.currentTarget, contact })
                        }
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
              <Menu
                anchorEl={rowMenu.anchorEl}
                open={Boolean(rowMenu.anchorEl)}
                onClose={() => setRowMenu({ anchorEl: null, contact: null })}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {renderRowMenuItems()}
              </Menu>
              {loading && (
                <Table size="small">
                  <TableBody>
                    <TableRowSkeleton avatar columns={1} />
                  </TableBody>
                </Table>
              )}
            </div>
          ) : (
            <Table stickyHeader size="small" aria-label="tabela de contatos" style={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>
                    {i18n.t("contacts.table.name")}
                  </TableCell>
                  <TableCell className={classes.headCell}>
                    {i18n.t("contacts.table.whatsapp")}
                  </TableCell>
                  <TableCell className={classes.headCell}>E-mail</TableCell>
                  <TableCell className={classes.headCell}>Etiquetas</TableCell>
                  <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>
                    {i18n.t("contacts.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id} className={classes.row}>
                    <TableCell style={{ paddingLeft: 20 }}>
                      <div
                        className={classes.nameCell}
                        onClick={() => handleOpenContactProfile(contact.id)}
                      >
                        <ContactAvatar src={contact.profilePicUrl} name={contact.name} />
                        <span className={classes.contactName}>{contact.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className={classes.phone}>
                      {formatPhone(contact.number) || (
                        <span className={classes.muted}>Sem WhatsApp</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.email ? (
                        contact.email
                      ) : (
                        <Button
                          color="primary"
                          className={classes.addEmailLink}
                          onClick={() => hadleEditContact(contact.id)}
                        >
                          + Adicionar
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.tags && contact.tags.length > 0 ? (
                        <ContactTagChips tags={contact.tags} />
                      ) : (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>
                    <TableCell style={{ paddingRight: 20 }}>
                      <div className={classes.actions}>
                        {renderChatButton(contact)}
                        {renderSecondaryActions(contact)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {contacts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} style={{ border: "none", padding: 0 }}>
                      {emptyState}
                    </TableCell>
                  </TableRow>
                )}
                {loading && <TableRowSkeleton avatar columns={3} />}
              </TableBody>
            </Table>
          )}
        </div>

        {contacts.length > 0 && (
          <div className={classes.footer}>
            {`Mostrando ${contacts.length}${
              totalCount !== null ? ` de ${totalCount}` : ""
            } ${contacts.length === 1 && totalCount === 1 ? "contato" : "contatos"}`}
            {!loading && !hasMore ? " · todos carregados" : ""}
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
